import { useTheme } from '@mui/material';
import DSBox from '../../../design-system/DSBox';
import DSButton from '../../../design-system/DSButton';
import DSTooltip from '../../../design-system/DSTooltip';
import { AFFILIATE_NOT_READY_SPEC } from '../../../consts/specs';
import { useAffiliateLinkState } from '../hooks/useAffiliateLinkState';
import AffiliateLinkModal from './AffiliateLinkModal';
import { ReactComponent as KlydoOutlineIcon } from '../../../design-system/Icons/KlydoOutlineIcon.svg';

type AffiliateLinkButtonProps = {
  collectionId: string;
  collectionName: string;
  hasAffiliateLink: boolean;
  cartImage?: string;
};

const AffiliateLinkButton = ({
  collectionId,
  collectionName,
  hasAffiliateLink,
  cartImage,
}: AffiliateLinkButtonProps) => {
  const { palette, spacing } = useTheme();
  const {
    isOpen,
    linkExists,
    notReady,
    generating,
    errored,
    ready,
    url,
    copied,
    scaledCartImage,
    openModal,
    closeModal,
    copyLink,
    viewPage,
    retry,
  } = useAffiliateLinkState(collectionId, hasAffiliateLink, cartImage);

  return (
    <>
      <DSTooltip
        title={notReady ? AFFILIATE_NOT_READY_SPEC : ''}
        placement="bottom"
      >
        <span>
          <DSButton
            variant="text"
            onClick={openModal}
            disabled={notReady}
            aria-label="Create Klydoclock Bundle"
            startIcon={<KlydoOutlineIcon width={20} height={20} />}
            sx={{
              flex: '0 0 auto',
              minWidth: 0,
              borderRadius: '999px',
              px: { xxs: spacing(1.25), md: spacing(2) },
              py: spacing(1),
              color: palette.white[90],
              backgroundColor: isOpen ? palette.white[12] : 'transparent',
              fontFamily: 'Gilroy-SemiBold',
              textTransform: 'none',
              whiteSpace: 'nowrap',
              transition: 'all 0.15s ease',
              '&:hover': { backgroundColor: palette.white[12] },
              '& .MuiButton-startIcon': {
                mr: { xxs: 0, md: 1 },
                ml: { xxs: 0, md: -0.5 },
              },
              '@keyframes affiliatePulse': {
                '0%': { boxShadow: '0 0 0 0 rgba(255,255,255,0.5)' },
                '70%': { boxShadow: '0 0 0 12px rgba(255,255,255,0)' },
                '100%': { boxShadow: '0 0 0 0 rgba(255,255,255,0)' },
              },
              animation:
                !linkExists && !notReady && !isOpen
                  ? 'affiliatePulse 1.8s ease-out infinite'
                  : 'none',
            }}
          >
            <DSBox
              component="span"
              sx={{ display: { xxs: 'none', md: 'inline' } }}
            >
              Create Klydoclock Bundle
            </DSBox>
          </DSButton>
        </span>
      </DSTooltip>

      <AffiliateLinkModal
        open={isOpen}
        onClose={closeModal}
        collectionName={collectionName}
        scaledCartImage={scaledCartImage}
        generating={generating}
        errored={errored}
        ready={ready}
        url={url}
        copied={copied}
        onCopy={copyLink}
        onViewPage={viewPage}
        onRetry={retry}
      />
    </>
  );
};

export default AffiliateLinkButton;
